import { useState, useEffect, useCallback } from 'react';
import { shiftsAPI, objectsAPI, usersAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Plus, Trash2, Loader2,
  Calendar, Settings, X, Copy, Building2, Palette
} from 'lucide-react';

const COLORS = ['#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899','#06B6D4','#84CC16'];

const HOURS = Array.from({length: 24}, (_, i) => String(i).padStart(2,'0'));
const MINS = ['00','15','30','45'];

function TimePicker({ value, onChange }) {
  const [h, m] = (value || '09:00').split(':');
  return (
    <div className="flex items-center gap-1">
      <select
        value={h}
        onChange={e => onChange(`${e.target.value}:${m}`)}
        className="font-sans bg-wp-dark/60 border border-white/10 text-white rounded-lg px-2 py-2 text-sm outline-none cursor-pointer hover:border-white/20 transition-colors"
      >
        {HOURS.map(hh => <option key={hh} value={hh}>{hh}</option>)}
      </select>
      <span className="text-white/40 font-medium">:</span>
      <select
        value={m}
        onChange={e => onChange(`${h}:${e.target.value}`)}
        className="font-sans bg-wp-dark/60 border border-white/10 text-white rounded-lg px-2 py-2 text-sm outline-none cursor-pointer hover:border-white/20 transition-colors"
      >
        {MINS.map(mm => <option key={mm} value={mm}>{mm}</option>)}
      </select>
    </div>
  );
}

const DAY_LABELS = {
  en: ['Mo','Tu','We','Th','Fr','Sa','Su'],
  ru: ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'],
  et: ['E','T','K','N','R','L','P'],
};

function getDayOfWeek(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return (d.getDay() + 6) % 7; // 0=Mon, 6=Sun
}

function isWeekend(dateStr) {
  const dow = getDayOfWeek(dateStr);
  return dow >= 5;
}

function getDaysInMonth(year, month) {
  const days = [];
  const count = new Date(year, month, 0).getDate();
  for (let d = 1; d <= count; d++) {
    const str = `${year}-${String(month).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    days.push(str);
  }
  return days;
}

export default function Schedule() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const isAdmin = ['admin','hr'].includes(user?.role);

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [objects, setObjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedObject, setSelectedObject] = useState('');
  const [loading, setLoading] = useState(true);

  // Cell popup state
  const [popup, setPopup] = useState(null); // { userId, date, existing: shift|null }
  const [popupStart, setPopupStart] = useState('09:00');
  const [popupEnd, setPopupEnd] = useState('18:00');
  const [popupObject, setPopupObject] = useState('');
  const [popupTemplate, setPopupTemplate] = useState('');
  const [saving, setSaving] = useState(false);

  // Template management
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [tmplName, setTmplName] = useState('');
  const [tmplStart, setTmplStart] = useState('09:00');
  const [tmplEnd, setTmplEnd] = useState('18:00');
  const [tmplColor, setTmplColor] = useState('#3B82F6');

  const days = getDaysInMonth(year, month);
  const monthLabel = new Date(year, month - 1, 1).toLocaleString(
    language === 'et' ? 'et-EE' : language === 'ru' ? 'ru-RU' : 'en-US',
    { month: 'long' }
  ).replace(/^./, c => c.toUpperCase()) + ' ' + year;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [shRes, empRes, objRes, tmplRes] = await Promise.all([
        shiftsAPI.list(month, year, selectedObject || undefined).catch(() => ({ data: { shifts: [] } })),
        usersAPI.list().catch(() => ({ data: [] })),
        objectsAPI.list().catch(() => ({ data: [] })),
        shiftsAPI.templates().catch(() => ({ data: [] })),
      ]);
      setShifts(shRes.data?.shifts || []);
      const allUsers = empRes.data || [];
      setEmployees(allUsers.filter(e => e.role === 'employee'));
      setObjects(objRes.data || []);
      setTemplates(tmplRes.data || []);
      if (!popupObject && objRes.data?.length > 0) setPopupObject(objRes.data[0].id);
    } catch {
      toast.error(t('common.error') || 'Error loading data');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year, selectedObject, t]);

  useEffect(() => { load(); }, [load]);

  // Build lookup: userId+date -> shift
  const shiftMap = {};
  shifts.forEach(s => {
    const key = `${s.user_id}__${s.date}`;
    if (!shiftMap[key]) shiftMap[key] = [];
    shiftMap[key].push(s);
  });

  // Show all employees — object selector in header filters which shifts are shown
  const visibleEmployees = employees;

  function openPopup(userId, date, existing) {
    setPopup({ userId, date, existing });
    setPopupStart(existing?.start_time || '09:00');
    setPopupEnd(existing?.end_time || '18:00');
    setPopupObject(existing?.object_id || (objects[0]?.id || ''));
    setPopupTemplate('');
  }

  function applyTemplate(tid) {
    setPopupTemplate(tid);
    const tmpl = templates.find(t => t.id === tid);
    if (tmpl) { setPopupStart(tmpl.start_time); setPopupEnd(tmpl.end_time); }
  }

  async function saveShift() {
    if (!popup) return;
    setSaving(true);
    try {
      const payload = {
        user_id: popup.userId,
        date: popup.date,
        start_time: popupStart,
        end_time: popupEnd,
        object_id: popupObject,
        template_id: popupTemplate || null,
      };
      if (popup.existing) {
        await shiftsAPI.update(popup.existing.id, { start_time: popupStart, end_time: popupEnd, object_id: popupObject });
      } else {
        await shiftsAPI.create(payload);
      }
      toast.success(t('schedule.saved') || 'Saved');
      setPopup(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  }

  async function deleteShift(shiftId) {
    try {
      await shiftsAPI.delete(shiftId);
      toast.success(t('schedule.deleted') || 'Deleted');
      setPopup(null);
      load();
    } catch { toast.error('Error'); }
  }

  async function copyWeek(userId) {
    // find current week in displayed month, copy to next week
    const today = new Date();
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - getDayOfWeek(today.toISOString().slice(0,10)));
    const assignments = [];
    for (let i = 0; i < 7; i++) {
      const src = new Date(weekStart); src.setDate(weekStart.getDate() + i);
      const srcStr = src.toISOString().slice(0,10);
      const existing = shiftMap[`${userId}__${srcStr}`];
      if (existing && existing.length > 0) {
        const s = existing[0];
        const dst = new Date(src); dst.setDate(dst.getDate() + 7);
        const dstStr = dst.toISOString().slice(0,10);
        assignments.push({ user_id: userId, date: dstStr, start_time: s.start_time, end_time: s.end_time, object_id: s.object_id });
      }
    }
    if (assignments.length === 0) { toast.error(t('schedule.noShiftsThisWeek') || 'No shifts this week'); return; }
    const res = await shiftsAPI.bulk(assignments);
    toast.success(`${res.data.created} ${t('schedule.copiedShifts') || 'shifts copied'}`);
    load();
  }

  async function fillWeekdays(userId) {
    if (!popupObject) { toast.error('Select object first'); return; }
    const assignments = days
      .filter(d => !isWeekend(d))
      .filter(d => !shiftMap[`${userId}__${d}`])
      .map(d => ({ user_id: userId, date: d, start_time: popupStart, end_time: popupEnd, object_id: popupObject }));
    if (assignments.length === 0) { toast.error(t('schedule.allFilled') || 'All weekdays already filled'); return; }
    const res = await shiftsAPI.bulk(assignments);
    toast.success(`${res.data.created} ${t('schedule.created') || 'shifts created'}`);
    load();
  }

  async function clearMonth(userId) {
    if (!window.confirm(t('schedule.confirmClear') || 'Delete all shifts for this employee this month?')) return;
    const toDelete = shifts.filter(s => s.user_id === userId);
    await Promise.all(toDelete.map(s => shiftsAPI.delete(s.id)));
    toast.success(t('schedule.cleared') || 'Cleared');
    load();
  }

  async function saveTemplate() {
    if (!tmplName.trim()) {
      toast.error(t('schedule.templateNameRequired') || 'Enter template name');
      return;
    }
    try {
      await shiftsAPI.createTemplate({ name: tmplName, start_time: tmplStart, end_time: tmplEnd, color: tmplColor });
      toast.success(t('schedule.templateSaved') || 'Template saved');
      setTmplName(''); setTmplStart('09:00'); setTmplEnd('18:00'); setTmplColor('#3B82F6');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error saving template');
    }
  }

  function prevMonth() { if (month === 1) { setMonth(12); setYear(y => y-1); } else setMonth(m => m-1); }
  function nextMonth() { if (month === 12) { setMonth(1); setYear(y => y+1); } else setMonth(m => m+1); }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-white/30" />
    </div>
  );

  return (
    <div className="space-y-6 pb-10">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-wp-medium/20 flex items-center justify-center">
            <Calendar className="h-5 w-5 text-wp-light" />
          </div>
          <p className="text-2xl font-light text-white">{t('nav.schedule') || 'Schedule'}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Object filter */}
          <select
            value={selectedObject}
            onChange={e => setSelectedObject(e.target.value)}
            className="font-sans bg-wp-dark/40 border border-wp-dark/50 text-white text-sm rounded-lg px-3 py-2 outline-none cursor-pointer hover:border-white/20 transition-colors"
          >
            <option value="">{t('schedule.allObjects') || 'All objects'}</option>
            {objects.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          {/* Templates button */}
          {isAdmin && (
            <button onClick={() => setShowTemplateDialog(true)} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10 text-sm transition-colors">
              <Settings className="h-4 w-4" />
              {t('schedule.templates') || 'Templates'}
            </button>
          )}
        </div>
      </div>

      {/* Month navigation */}
      <div className="glass-card rounded-2xl p-4">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-white transition-colors">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <p className="text-lg font-medium text-white capitalize flex-1 text-center">{monthLabel}</p>
          <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-white transition-colors">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {visibleEmployees.length === 0 ? (
          <p className="text-center text-white/30 py-12">{t('schedule.noEmployees') || 'No employees found'}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr>
                  <th className="text-left text-white/50 font-medium py-2 pr-3 w-32 sticky left-0 bg-wp-darker z-10">
                    {t('common.name') || 'Name'}
                  </th>
                  {days.map(d => {
                    const day = parseInt(d.slice(8));
                    const dow = getDayOfWeek(d);
                    const lang = language === 'et' ? 'et' : language === 'ru' ? 'ru' : 'en';
                    const isWknd = dow >= 5;
                    const isToday = d === new Date().toISOString().slice(0,10);
                    return (
                      <th key={d} className={`text-center font-normal py-1 px-0.5 min-w-[36px] ${isWknd ? 'text-white/20' : 'text-white/40'}`}>
                        <div className={`text-[10px] ${isWknd ? 'text-white/20' : 'text-white/30'}`}>{DAY_LABELS[lang][dow]}</div>
                        <div className={`w-6 h-6 mx-auto rounded-full flex items-center justify-center text-xs font-medium ${isToday ? 'bg-wp-medium text-white' : ''}`}>{day}</div>
                      </th>
                    );
                  })}
                  {isAdmin && <th className="w-20"></th>}
                </tr>
              </thead>
              <tbody>
                {visibleEmployees.map(emp => (
                  <tr key={emp.id} className="border-t border-white/5 group">
                    <td className="py-2 pr-3 sticky left-0 bg-wp-darker z-10">
                      <span className="text-white/80 font-medium truncate block max-w-[120px]">{emp.name}</span>
                    </td>
                    {days.map(d => {
                      const cellShifts = shiftMap[`${emp.id}__${d}`] || [];
                      const isWknd = isWeekend(d);
                      return (
                        <td key={d} className="py-1 px-0.5 text-center min-w-[52px]">
                          {cellShifts.length > 0 ? (
                            <div className="flex flex-col gap-0.5">
                              {cellShifts.map((shift, si) => {
                                const tmpl = shift?.template_id ? templates.find(t => t.id === shift.template_id) : null;
                                const color = tmpl?.color || '#3B82F6';
                                return (
                                  <button
                                    key={shift.id || si}
                                    onClick={() => isAdmin && openPopup(emp.id, d, shift)}
                                    className="w-full rounded-md px-1 py-0.5 text-white text-[10px] font-medium leading-tight transition-opacity hover:opacity-80"
                                    style={{ backgroundColor: color + '33', borderLeft: `2px solid ${color}` }}
                                  >
                                    <div>{shift.start_time}</div>
                                    <div>{shift.end_time}</div>
                                  </button>
                                );
                              })}
                              {isAdmin && cellShifts.length < 2 && (
                                <button
                                  onClick={() => openPopup(emp.id, d, null)}
                                  className="w-full h-5 rounded-md transition-colors hover:bg-white/10 text-white/30 hover:text-white/60"
                                  title="Add second shift"
                                >
                                  <Plus className="h-2.5 w-2.5 mx-auto" />
                                </button>
                              )}
                            </div>
                          ) : (
                            <button
                              onClick={() => isAdmin && openPopup(emp.id, d, null)}
                              className={`w-full h-8 rounded-md transition-colors ${isAdmin ? 'hover:bg-white/10 cursor-pointer' : 'cursor-default'} ${isWknd ? 'opacity-30' : ''}`}
                            >
                              {isAdmin && <Plus className="h-3 w-3 text-white/20 mx-auto group-hover:text-white/40" />}
                            </button>
                          )}
                        </td>
                      );
                    })}
                    {isAdmin && (
                      <td className="py-1 pl-2">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button title={t('schedule.copyWeek') || 'Copy week'} onClick={() => copyWeek(emp.id)} className="p-1 rounded hover:bg-white/10 text-white/30 hover:text-white transition-colors">
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                          <button title={t('schedule.clearMonth') || 'Clear month'} onClick={() => clearMonth(emp.id)} className="p-1 rounded hover:bg-red-500/20 text-white/30 hover:text-red-400 transition-colors">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend */}
      {templates.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {templates.map(tmpl => (
            <div key={tmpl.id} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tmpl.color }} />
              <span className="text-xs text-white/60">{tmpl.name}</span>
              <span className="text-xs text-white/30">{tmpl.start_time}–{tmpl.end_time}</span>
            </div>
          ))}
        </div>
      )}

      {/* Cell popup */}
      <Dialog open={!!popup} onOpenChange={open => !open && setPopup(null)}>
        <DialogContent className="bg-wp-darker border-wp-dark/50 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white font-medium">
              {popup && employees.find(e => e.id === popup.userId)?.name} — {popup?.date}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {templates.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-white/60 text-xs">{t('schedule.template') || 'Template'}</Label>
                <Select value={popupTemplate} onValueChange={applyTemplate}>
                  <SelectTrigger className="bg-wp-dark/40 border-wp-dark/50 text-white">
                    <SelectValue placeholder={t('schedule.selectTemplate') || 'Select template…'} />
                  </SelectTrigger>
                  <SelectContent className="bg-wp-darker border-wp-dark/50">
                    {templates.map(tmpl => (
                      <SelectItem key={tmpl.id} value={tmpl.id} className="text-white hover:bg-white/10">
                        <span className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: tmpl.color }} />
                          {tmpl.name} ({tmpl.start_time}–{tmpl.end_time})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-white/60 text-xs">{t('schedule.startTime') || 'Start'}</Label>
                <TimePicker value={popupStart} onChange={setPopupStart} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/60 text-xs">{t('schedule.endTime') || 'End'}</Label>
                <TimePicker value={popupEnd} onChange={setPopupEnd} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">{t('nav.objects') || 'Object'}</Label>
              <Select value={popupObject} onValueChange={setPopupObject}>
                <SelectTrigger className="bg-wp-dark/40 border-wp-dark/50 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wp-darker border-wp-dark/50">
                  {objects.map(o => (
                    <SelectItem key={o.id} value={o.id} className="text-white hover:bg-white/10">{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-1">
              {popup?.existing && (
                <button onClick={() => deleteShift(popup.existing.id)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 text-sm transition-colors">
                  <Trash2 className="h-4 w-4" />
                  {t('common.delete') || 'Delete'}
                </button>
              )}
              <div className="flex gap-2 ml-auto">
                {!popup?.existing && (
                  <button onClick={() => fillWeekdays(popup?.userId)}
                    className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:text-white text-sm transition-colors">
                    {t('schedule.fillWeekdays') || 'Fill Pn–Pt'}
                  </button>
                )}
                <button onClick={saveShift} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-wp-medium text-white hover:bg-wp-medium/80 text-sm transition-colors disabled:opacity-50">
                  {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {t('common.save') || 'Save'}
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template management dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="bg-wp-darker border-wp-dark/50 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-medium flex items-center gap-2">
              <Palette className="h-4 w-4 text-wp-light" />
              {t('schedule.manageTemplates') || 'Shift Templates'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Existing templates */}
            {templates.length > 0 && (
              <div className="space-y-2">
                {templates.map(tmpl => (
                  <div key={tmpl.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/5 border border-white/5">
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: tmpl.color }} />
                    <span className="flex-1 text-sm text-white">{tmpl.name}</span>
                    <span className="text-xs text-white/40">{tmpl.start_time}–{tmpl.end_time}</span>
                    <button onClick={async () => { await shiftsAPI.deleteTemplate(tmpl.id); load(); }}
                      className="p-1 rounded hover:bg-red-500/20 text-white/30 hover:text-red-400 transition-colors">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* New template form */}
            <div className="border-t border-white/10 pt-4 space-y-3">
              <p className="text-xs text-white/40 uppercase tracking-wider">{t('schedule.newTemplate') || 'New template'}</p>
              <div className="space-y-1.5">
                <Label className="text-white/60 text-xs">{t('schedule.templateName') || 'Name'}</Label>
                <input value={tmplName} onChange={e => setTmplName(e.target.value)} placeholder="e.g. Morning shift"
                  className="w-full bg-wp-dark/40 border border-wp-dark/50 text-white rounded-lg px-3 py-2 text-sm outline-none placeholder:text-white/20" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-white/60 text-xs">{t('schedule.startTime') || 'Start'}</Label>
                  <TimePicker value={tmplStart} onChange={setTmplStart} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-white/60 text-xs">{t('schedule.endTime') || 'End'}</Label>
                  <TimePicker value={tmplEnd} onChange={setTmplEnd} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/60 text-xs">{t('schedule.color') || 'Color'}</Label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map(c => (
                    <button key={c} onClick={() => setTmplColor(c)}
                      className={`w-7 h-7 rounded-full transition-transform ${tmplColor === c ? 'scale-125 ring-2 ring-white/50' : ''}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
              <button onClick={saveTemplate} className="w-full py-2 rounded-lg bg-wp-medium text-white text-sm hover:bg-wp-medium/80 transition-colors">
                <Plus className="h-4 w-4 inline mr-1" />
                {t('schedule.addTemplate') || 'Add template'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
